import React, { ChangeEvent, useEffect, useState } from "react";

import { color } from "minimum-ui/tokens";
import styled from "styled-components";
import { H4M } from "typography/headers";
import { useDebouncedCallback } from "use-debounce";

export interface TextAreaProps {
  label?: string | React.ReactNode;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  value?: string;
  placeholder?: string;
  className?: string;
  rows?: number;
  disabled?: boolean;
  debounce?: boolean;
  debounceTime?: number;
}

const TextArea: React.FC<TextAreaProps> = ({
  rows,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  debounce,
  debounceTime,
}) => {
  const [internalValue, setInternalValue] = useState<string>(value || "");

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInternalValue(e.target.value);
    //if the input doesn't have a debounce then we should call onchange instantly
    if (onChange && !debounce) {
      onChange(e);
    }
  };

  const debouncedOnChange = useDebouncedCallback(() => {
    if (onChange && internalValue !== value) {
      // @ts-ignore
      onChange({ target: { value: internalValue } });
    }
  }, debounceTime || 500);

  useEffect(() => {
    if (debounce) {
      debouncedOnChange();
    }
  }, [debounce, debouncedOnChange, internalValue]);

  return (
    <div>
      {label && <Label> {label}</Label>}
      <Component
        rows={rows || 5}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
};

const Label = styled(H4M)`
  color: ${color.neutral[400]};
  margin-bottom: 6px;
`;

const Component = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background-color: ${color.basic.white};
  color: ${color.neutral[500]};
  border: 1px solid ${color.border.default};
  outline: none;
  border-radius: 8px;

  font-family: "InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI",
    "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
    "Helvetica Neue", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;

  &:active {
    border: 1px solid ${color.blue[300]};
  }

  &:focus {
    border: 1px solid ${color.blue[300]};
  }

  &:disabled {
    background-color: ${color.neutral[100]};
    box-shadow: none;
    border-color: ${color.neutral[400]};
    cursor: not-allowed;
    color: ${color.neutral[400]};
  }
`;

export default TextArea;
